/* ==========================================================================
   wd-scrollbar.js
   Always-visible custom scrollbar for wd-* sections.

   Native overlay scrollbars auto-hide (macOS, iOS, Android, Chrome overlay
   mode) and ::-webkit-scrollbar is ignored on touch-scrolled elements, so a
   CSS-only bar can never be guaranteed visible. This draws its own track +
   thumb and lays it over the scroll box.

   Usage — mark up any scrollable element:
     <div class="..." data-wd-scrollbar data-wd-scrollbar-side="left|right">

   The bar appears only while the element is genuinely scrollable
   (overflow-y auto/scroll AND scrollHeight > clientHeight), and stays
   visible the whole time — it is never hover-gated.

   The track is inserted as the scroll box's NEXT SIBLING, not inside it. An
   absolutely positioned child counts toward its scroll container's scrollable
   overflow, so pinning it with translateY(scrollTop) drags its bottom edge
   down as you scroll, growing scrollHeight and letting the box scroll past the
   end of its own content without ever reaching a fixed point. Outside the box
   the track cannot affect scrollHeight at all, and it needs no per-frame
   transform — only the thumb moves.

   WHERE the track sits is owned by CSS in the consuming section, never by this
   file. The section already knows its own layout, and a CSS offset cannot go
   stale; JS-computed coordinates did, every time something above the host
   reflowed. Each section must give the track's parent a positioned containing
   block and set the track's own offsets. This file only sets width, height,
   colours and the thumb.

   Thumb length is proportional (viewport / content) but clamped between
   --wd-sb-thumb-min and --wd-sb-thumb-max so it always reads as a moving
   indicator. A strictly proportional thumb fills ~80% of the track when the
   overflow is small, which looks like a plain bar with nothing in it.

   jQuery (3.6.0, bundled in assets/vendor.js) to match the theme's
   mrk.js-era conventions.
   ========================================================================== */

(function ($) {
  'use strict';

  /* Sections include this asset individually — only wire up once. */
  if (!$ || window.wdScrollbarLoaded) { return; }
  window.wdScrollbarLoaded = true;

  var SELECTOR      = '[data-wd-scrollbar]';
  var DEF_THUMB_MIN = 28;    /* px    — floor, so a long scroll range stays grabbable */
  var DEF_THUMB_MAX = 0.4;   /* ratio — ceiling, as a fraction of the track height */
  var DEF_WIDTH     = 6;     /* px */
  var bars          = [];

  function createBar(host) {
    var $host = $(host);

    if ($host.data('wdSbBound')) { return null; }
    $host.data('wdSbBound', true);

    var side   = $host.attr('data-wd-scrollbar-side') === 'left' ? 'left' : 'right';
    var $track = $('<div class="wd-sb-track wd-sb-track-' + side + '" aria-hidden="true"></div>');
    var $thumb = $('<div class="wd-sb-thumb"></div>').appendTo($track);
    var track  = $track[0];
    var thumb  = $thumb[0];

    /* Sibling, never a child — see the header note on scrollHeight. As
       siblings the two share an offsetParent, so the host's offsetTop /
       offsetLeft place the track over it exactly. */
    $host.addClass('wd-sb-host').after($track);

    /* Cached metrics — refreshed by measure(), read by position(). */
    var scrollable = false;
    var range      = 0;   /* scrollHeight - clientHeight */
    var thumbH     = 0;
    var travel     = 0;   /* clientHeight - thumbH */
    var pending    = null;

    /* Cheap: only reads scrollTop. Runs synchronously on every scroll tick so
       the thumb tracks the content with no visible lag. */
    function position() {
      if (!scrollable) { return; }

      var top = host.scrollTop;

      thumb.style.transform =
        'translateY(' + (travel > 0 ? Math.round((top / range) * travel) : 0) + 'px)';
    }

    /* Read a custom property, returning '' when it is unset OR set to an empty
       value. Never hand an empty string back to CSS: a background declared as
       var(--x, fallback) where --x is defined but empty resolves to nothing,
       which invalidates the declaration at computed-value time and paints the
       element transparent — the fallback is NOT used. That is why the
       stylesheet holds literals and these are applied here only when they
       actually carry a value. */
    function readVar(styles, name) {
      var value = styles.getPropertyValue(name);

      return value ? value.trim() : '';
    }

    function applyTheme(styles) {
      var radius = readVar(styles, '--wd-sb-radius');
      var trackC = readVar(styles, '--wd-sb-track-color');
      var thumbC = readVar(styles, '--wd-sb-thumb-color');

      if (radius) { track.style.borderRadius = radius; }
      if (trackC) { track.style.backgroundColor = trackC; }
      if (thumbC) { thumb.style.backgroundColor = thumbC; }
    }

    /* Size only — never position. Where the track sits is owned entirely by
       CSS in the consuming section, because each section already knows its own
       layout and a CSS offset cannot go stale. Computing top/left here meant
       writing absolute coordinates that silently broke whenever anything above
       the host reflowed (a lazy image finishing load further up the page):
       ResizeObserver reports size changes, not position changes, so nothing
       ever told us to re-sync. Height still comes from JS since it has to
       track the host's clientHeight. */
    function layout(styles) {
      var width = parseFloat(readVar(styles, '--wd-sb-width'));

      if (isNaN(width) || width <= 0) { width = DEF_WIDTH; }

      track.style.width  = width + 'px';
      track.style.height = host.clientHeight + 'px';
    }

    function measure() {
      pending = null;

      var styles   = window.getComputedStyle(host);
      var overflow = styles.overflowY;
      var clientH  = host.clientHeight;
      var scrollH  = host.scrollHeight;

      scrollable = (overflow === 'auto' || overflow === 'scroll') &&
                   (scrollH - clientH) > 1;

      $host.toggleClass('wd-sb-active', scrollable);
      $track.toggleClass('wd-sb-on', scrollable);

      /* Lay out even when hidden, so a slide that only becomes scrollable
         later never flashes stale geometry from an earlier measure. */
      applyTheme(styles);
      layout(styles);

      if (!scrollable) { return; }

      var maxRatio = parseFloat(styles.getPropertyValue('--wd-sb-thumb-max')) || DEF_THUMB_MAX;
      var minPx    = parseFloat(styles.getPropertyValue('--wd-sb-thumb-min')) || DEF_THUMB_MIN;

      range = scrollH - clientH;

      /* Proportional length, then clamped. A strictly proportional thumb fills
         ~80% of the track when the overflow is small, which reads as a plain
         bar with no indicator in it — clamping keeps it legible as a marker. */
      thumbH = Math.round((clientH * clientH) / scrollH);
      thumbH = Math.min(thumbH, Math.round(clientH * maxRatio));
      thumbH = Math.max(thumbH, minPx);
      thumbH = Math.min(thumbH, clientH);

      travel = clientH - thumbH;

      thumb.style.height = thumbH + 'px';

      position();
    }

    function schedule() {
      if (pending) { return; }
      pending = window.requestAnimationFrame(measure);
    }

    /* ───── Scroll ───── */
    host.addEventListener('scroll', position, { passive: true });

    /* ───── Thumb drag (mouse + touch) ───── */
    var dragging = false;
    var startY   = 0;
    var startTop = 0;

    function pointerY(e) {
      var src = e.originalEvent || e;

      if (src.touches && src.touches.length) { return src.touches[0].clientY; }

      return src.clientY;
    }

    function dragStart(e) {
      if (!scrollable || travel <= 0) { return; }

      dragging = true;
      startY   = pointerY(e);
      startTop = host.scrollTop;

      $host.addClass('wd-sb-dragging');
      e.preventDefault();
      e.stopPropagation();
    }

    function dragMove(e) {
      if (!dragging) { return; }

      host.scrollTop = startTop + (pointerY(e) - startY) * (range / travel);
      e.preventDefault();
    }

    function dragEnd() {
      if (!dragging) { return; }

      dragging = false;
      $host.removeClass('wd-sb-dragging');
    }

    $thumb.on('mousedown', dragStart);
    /* Non-passive so the page behind does not scroll while dragging. */
    thumb.addEventListener('touchstart', dragStart, { passive: false });

    $(document).on('mousemove', dragMove).on('mouseup', dragEnd);
    document.addEventListener('touchmove', dragMove, { passive: false });
    document.addEventListener('touchend', dragEnd);
    document.addEventListener('touchcancel', dragEnd);

    /* ───── Click on the track jumps to that position ───── */
    $track.on('mousedown', function (e) {
      if (e.target !== track || !scrollable || travel <= 0) { return; }

      var rect  = track.getBoundingClientRect();
      var ratio = (e.clientY - rect.top - thumbH / 2) / travel;

      host.scrollTop = Math.max(0, Math.min(1, ratio)) * range;
      e.preventDefault();
    });

    /* ───── Keep in sync with content / layout changes ─────
       ResizeObserver covers the host, its children (FAQ answers expanding) and
       its parent (the host shifting inside the offsetParent); MutationObserver
       covers blocks added or removed; transitionend covers the FAQ max-height
       animation on browsers without ResizeObserver. */
    var resizeObserver = null;

    function observeChildren() {
      if (!resizeObserver) { return; }

      $host.children().each(function () {
        resizeObserver.observe(this);
      });
    }

    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(schedule);
      resizeObserver.observe(host);

      if (host.parentElement) { resizeObserver.observe(host.parentElement); }

      observeChildren();
    }

    if (window.MutationObserver) {
      /* childList only — attribute writes are ours, watching them would loop. */
      new window.MutationObserver(function () {
        observeChildren();
        schedule();
      }).observe(host, { childList: true, subtree: true });
    }

    $host.on('transitionend animationend', schedule);

    measure();

    var bar = {
      host: host,
      refresh: schedule,
      info: function () {
        var styles = window.getComputedStyle(thumb);

        return {
          host: host.className,
          active: $host.hasClass('wd-sb-active'),
          clientH: host.clientHeight,
          scrollH: host.scrollHeight,
          trackH: track.offsetHeight,
          thumbH: thumb.offsetHeight,
          thumbBg: styles.backgroundColor,
          insideScroller: track.parentElement === host,
          thumbVisible: thumb.offsetHeight > 0 &&
                        styles.display !== 'none' &&
                        styles.backgroundColor !== 'rgba(0, 0, 0, 0)'
        };
      }
    };

    bars.push(bar);

    return bar;
  }

  function initAll(root) {
    $(root || document).find(SELECTOR).addBack(SELECTOR).each(function () {
      createBar(this);
    });
  }

  function refreshAll() {
    for (var i = 0; i < bars.length; i++) { bars[i].refresh(); }
  }

  function debug() {
    return bars.map(function (bar) { return bar.info(); });
  }

  $(function () { initAll(); });

  /* Late-loading images / fonts change scrollHeight. */
  $(window).on('load', refreshAll).on('resize orientationchange', refreshAll);

  /* Position, unlike size, is not observable. Anything reflowing between the
     containing block and the host shifts the host without resizing it, so
     ResizeObserver never fires. Re-sync when the document grows and whenever a
     resource finishes loading — capture phase, because load does not bubble. */
  if (window.ResizeObserver) {
    new window.ResizeObserver(refreshAll).observe(document.documentElement);
  }

  document.addEventListener('load', refreshAll, true);

  /* Theme editor re-renders a section: pick up the new markup. */
  $(document).on('shopify:section:load', function (e) { initAll(e.target); });

  window.wdScrollbar = { init: initAll, refresh: refreshAll, debug: debug };
})(window.jQuery);
